import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold tracking-[-0.01em] transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:     "bg-primary text-primary-foreground shadow-[0_1px_2px_hsl(var(--primary)/0.24),0_4px_12px_hsl(var(--primary)/0.12)] hover:brightness-105 hover:shadow-[0_2px_4px_hsl(var(--primary)/0.22),0_6px_16px_hsl(var(--primary)/0.14)] active:translate-y-px active:shadow-sm",
        destructive: "bg-destructive text-destructive-foreground shadow-[0_1px_2px_hsl(var(--destructive)/0.2)] hover:brightness-105 hover:shadow-md active:translate-y-px active:shadow-sm",
        outline:     "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/20 active:translate-y-px",
        secondary:   "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 active:translate-y-px",
        ghost:       "hover:bg-accent hover:text-accent-foreground",
        link:        "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // Horizontal padding on each size is chosen to sit visibly
        // clear of the rounded corner so text doesn't read as
        // clipped at the pill edge. Rule of thumb:
        // padding >= border-radius + ~4px perceptual daylight
        // (same principle the Input component landed on).
        //   default (rounded-xl=12px)  →  px-6 = 24px
        //   sm      (rounded-lg=8px)   →  px-4 = 16px
        //   lg      (rounded-xl=12px)  →  px-8 = 32px
        default: "h-10 px-6 py-2",
        sm:      "h-8 rounded-lg px-4 text-xs",
        lg:      "h-11 rounded-xl px-8 text-base",
        icon:    "h-10 w-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
